import { HiCheck } from 'react-icons/hi2'
import { FormChoiceScreen } from './types'
import { resolveIcon } from './iconResolver'

type Props = {
  step: FormChoiceScreen
  stepNumber: number // 1-indexed for "Step 1", "Step 2", etc.
  totalSteps: number
  isFirstStep: boolean
  isLastStep: boolean
  selectedOptionIds: number[]
  onChange: (optionIds: number[]) => void
  onNext: () => void
  onPrevious: () => void
  onBackToHomepage: () => void
  selectedUserName: string
  isSubmitting: boolean
}

// Renders one choice step. Single = radio semantics (selecting one
// replaces any prior selection). Multi = checkbox semantics. Both
// require explicit Next/Submit — no auto-advance.
export default function ChoiceStep({
  step,
  stepNumber,
  totalSteps,
  isFirstStep,
  isLastStep,
  selectedOptionIds,
  onChange,
  onNext,
  onPrevious,
  onBackToHomepage,
  selectedUserName,
  isSubmitting,
}: Props) {
  const isSingle = step.question_type === 'single'

  const toggleOption = (optionId: number) => {
    if (isSingle) {
      onChange([optionId])
    } else if (selectedOptionIds.includes(optionId)) {
      onChange(selectedOptionIds.filter((id) => id !== optionId))
    } else {
      onChange([...selectedOptionIds, optionId])
    }
  }

  const canAdvance = selectedOptionIds.length > 0
  const nextLabel = isLastStep ? (isSubmitting ? 'Submitting…' : 'Submit') : 'Next'

  return (
    <div className="min-h-screen w-full bg-[#f9fafb] text-[#0c0a08]">
      <div className="relative mx-auto flex min-h-screen w-full max-w-[800px] flex-col">
        <header className="flex items-start justify-between px-12 pt-5 pb-10">
          <button
            type="button"
            onClick={onBackToHomepage}
            className="rounded-md bg-[#e5e7eb] px-4 py-3 text-sm font-semibold text-[#0c0a08]/60 transition hover:bg-[#d1d5db]"
          >
            Back to Homepage
          </button>
          <div className="rounded-md border border-[#d1d5db] px-4 py-3 text-sm font-semibold uppercase tracking-wide text-black">
            {selectedUserName}
          </div>
        </header>

        <div className="flex gap-2 px-12">
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map((n) => (
            <div key={n} className="flex-1">
              <div
                className={`h-1 w-full ${n <= stepNumber ? 'bg-[#004f51]' : 'bg-[#d1d5db]'}`}
                aria-current={n === stepNumber ? 'step' : undefined}
              />
              {n === stepNumber && (
                <p className="pt-4 font-display text-xs font-semibold uppercase tracking-[0.3em] text-[#004f51]">
                  Step {n}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="px-12 pt-5">
          <h1 className="font-display text-5xl leading-[1.15] text-[#004f51]">{step.text}</h1>
          {step.description && (
            <p className="mt-5 font-body text-lg text-[#111d1e]">{step.description}</p>
          )}
        </div>

        <div className="flex flex-1 flex-col gap-12 px-12 py-12">
          <div className="flex flex-wrap gap-4">
            {step.options.map((opt, idx) => {
              const isSelected = selectedOptionIds.includes(opt.id)
              const icon = resolveIcon(opt.icon, 'h-7 w-7 text-[#004f51]')

              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => toggleOption(opt.id)}
                  className={`flex w-80 flex-col gap-5 rounded-xl p-6 text-left shadow-[0px_10px_20px_0px_rgba(189,205,199,0.5)] transition ${
                    isSelected ? 'border-2 border-[#00bed6] bg-[#eaf4d5]' : 'border-2 border-transparent bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div
                      className="flex h-16 w-16 items-center justify-center rounded-lg border border-white/40 backdrop-blur-sm"
                      style={{ background: 'rgba(147,213,0,0.23)', boxShadow: '2px 2px 7px 0px rgba(147,213,0,0.5)' }}
                    >
                      {icon || (
                        <span className="font-display text-2xl font-medium text-[#004f51]">{idx + 1}</span>
                      )}
                    </div>
                    {isSingle ? (
                      <span
                        className={`h-6 w-6 rounded-full border ${
                          isSelected ? 'border-[#004f51] bg-[#004f51]' : 'border-[#d1d5db] bg-white'
                        }`}
                        aria-hidden="true"
                      />
                    ) : (
                      <span
                        className={`flex h-6 w-6 items-center justify-center rounded border ${
                          isSelected ? 'border-[#004f51] bg-[#004f51]' : 'border-[#d1d5db] bg-white'
                        }`}
                        aria-hidden="true"
                      >
                        {isSelected && <HiCheck className="h-4 w-4 text-white" />}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <p className="font-display text-2xl font-medium text-[#004f51]">{opt.label}</p>
                    {opt.description && (
                      <p className="font-body text-base text-[#0c0a08]/60">{opt.description}</p>
                    )}
                  </div>
                </button>
              )
            })}
          </div>

          <div className="flex justify-end gap-2">
            {!isFirstStep && (
              <button
                type="button"
                onClick={onPrevious}
                className="rounded-md border border-[#0c0a08]/40 bg-white px-8 py-4 font-display text-xl font-semibold text-[#111d1e]"
              >
                Previous
              </button>
            )}
            <button
              type="button"
              onClick={onNext}
              disabled={!canAdvance || isSubmitting}
              className="flex items-center gap-2 rounded-md bg-[#111d1e] px-8 py-4 font-display text-xl font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-40 enabled:hover:bg-black"
            >
              {nextLabel}
              <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
